import { useQuery } from "@tanstack/react-query";
import { api } from "./client";

export interface ProgrammeUnitSummary {
  id: string;
  name: string;
  code: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  annualBudget: string;
  active: boolean;
  ytdIncome?: string;
  ytdPaid?: string;
  ytdNet?: string;
}

export interface ProgrammeGroupSummary {
  id: string;
  name: string;
  code: string;
  units: ProgrammeUnitSummary[];
}

export interface ProgrammesOverviewResponse {
  groups: ProgrammeGroupSummary[];
  unitCount: number;
  scope: "all" | string[];
  period?: string;
}

export interface OverviewTotalsResponse {
  overview: {
    period: string;
    periodStart: string;
    periodEnd: string;
    ytdStart: string;
    unitCount: number;
    totals: {
      periodIncome: string;
      periodPaid: string;
      ytdIncome: string;
      ytdPaid: string;
      approvedUnpaid: string;
      bankTotal?: string;
      bankAccounts?: { name: string; kind?: string; balance: string }[];
    };
    programmes: {
      unitId: string;
      unitName: string;
      groupName: string;
      annualBudget: string;
      periodIncome: string;
      periodPaid: string;
      periodNet: string;
      ytdIncome: string;
      ytdPaid: string;
      ytdNet: string;
    }[];
  };
}

export interface OverviewAttentionResponse {
  attention: {
    pendingApprovals: {
      count: number;
      totalAmount: string;
      items: {
        id: string;
        referenceNo: string;
        payee: string;
        expenseDate: string;
        total: string;
        status: string;
      }[];
    };
    missingEvidence: {
      count: number;
      items: {
        id: string;
        referenceNo: string;
        payee: string;
        expenseDate: string;
        total: string;
        status: string;
      }[];
    };
  };
}

export interface MonthlyOverviewResponse {
  months: { month: string; income: string; paid: string }[];
  period: string;
}

export interface UnitMovementsResponse {
  movements: {
    id: string;
    kind: "income" | "expense";
    date: string;
    voucher: string;
    description: string;
    amount: string;
  }[];
  period: string;
}

export function useProgrammesOverviewQuery(enabled = true, period?: string) {
  return useQuery({
    queryKey: ["overview", "programmes", period],
    queryFn: () =>
      api.get<ProgrammesOverviewResponse>(`/overview/programmes?period=${period}`),
    enabled: enabled && Boolean(period),
  });
}

export function useOverviewTotalsQuery(enabled = true, period?: string) {
  return useQuery({
    queryKey: ["overview", "totals", period],
    queryFn: () => api.get<OverviewTotalsResponse>(`/overview?period=${period}`),
    enabled: enabled && Boolean(period),
  });
}

export function useOverviewAttentionQuery(enabled = true) {
  return useQuery({
    queryKey: ["overview", "attention"],
    queryFn: () => api.get<OverviewAttentionResponse>("/overview/attention"),
    enabled,
  });
}

export function useOverviewMonthlyQuery(enabled = true, period?: string) {
  return useQuery({
    queryKey: ["overview", "monthly", period],
    queryFn: () => api.get<MonthlyOverviewResponse>(`/overview/monthly?period=${period}`),
    enabled: enabled && Boolean(period),
  });
}

export function useUnitMovementsQuery(unitId: string | null, period?: string) {
  return useQuery({
    queryKey: ["units", unitId, "movements", period],
    queryFn: () =>
      api.get<UnitMovementsResponse>(`/units/${unitId}/movements?period=${period}`),
    enabled: Boolean(unitId) && Boolean(period),
  });
}
